<template>
  <div
    ref="buttonSplit"
    :class="containerClasses"
  >
    <menu-wrapper 
      :class="menuWrapperClasses" 
      :disabled="isDisabled" 
      :class-root="menuWrapperRootClass"
      :closeOnClickItem="false"
    >
      <template #menu-button="{toggleMenu}">
        <ms-button
          :class="buttonClasses"
          :disabled="isDisabled"
          @click.prevent="toggleMenu"
          right-icon="down-arrow-combobox-white"
          :text="text"
        ></ms-button>
      </template>
      <template #menu-content>
        <menu-item 
          v-for="(item, index) in data" 
          :key="index" 
          @menu-item-click="clickItem(item)"
        >
          <div :class="optionItemClasses">
            <div :class="checkboxCellClasses">
              <div :class="getItemIconClasses(item)"></div>
            </div>
            <div>
              {{ item[effectiveDisplayField] }}
            </div>
          </div>
        </menu-item>
      </template>
    </menu-wrapper>
  </div>
</template>
<script>
export default {
  name: 'MsMultipleSelectButton'
}
</script>

<script setup>
import { ref } from 'vue'
import MenuWrapper from '@/components/dropdown/MenuWrapper.vue'
import MenuItem from '@/components/dropdown/MenuItem.vue'
import MsButton from '@/components/button/MsButton.vue'

// Import composables
import { useMultipleSelectButton } from './multiple-select-button/composables/useMultipleSelectButton.js'
import { useMultipleSelectButtonEvents } from './multiple-select-button/composables/useMultipleSelectButtonEvents.js'
import { useMultipleSelectButtonStyles } from './multiple-select-button/composables/useMultipleSelectButtonStyles.js'
import { DEFAULT_PROPS, VALID_MULTIPLE_SELECT_BUTTON_TYPES, EVENTS } from './multiple-select-button/constants/multiple-select-button-constants.js'

/**
 * MsMultipleSelectButton Component - Refactored từ code gốc giữ nguyên logic
 */

/**
 * Component props definition - giữ nguyên các props gốc
 */
const props = defineProps({
  // Text hiển thị
  text: {
    defualt: null, // Giữ nguyên typo từ code gốc
    type: String,
  },
  // Icon trái
  leftIcon: {
    default: DEFAULT_PROPS.leftIcon,
    type: String,
  },
  // Icon phải
  rightIcon: {
    default: DEFAULT_PROPS.rightIcon,
    type: String,
  },
  // Loại button gồm 3 loại primary/secondary/third/four để hiện thị style tương ứng
  type: {
    default: DEFAULT_PROPS.type,
    type: String,
    validator: (value) => VALID_MULTIPLE_SELECT_BUTTON_TYPES.includes(value)
  },
  /**
   * Trạng thái button disabled
   */
  disabled: {
    default: DEFAULT_PROPS.disabled,
    type: Boolean,
  },
  /**
   * Danh sách các item của context menu
   */
  data: {
    type: Array,
    default: () => DEFAULT_PROPS.data,
  },
  /**
   * ĐVThi 18/02/2020
   * muốn đặt thêm stype cho lớp mục đích có cách để trên form tìm được đến control
   */
  classCustom: {
    default: DEFAULT_PROPS.classCustom,
    type: String,
  },
  /**
   * NNLAM 26/02/2020
   * có cho phép tự custom vị trí dropdown menu hay không
   */
  customPositionMenu: {
    type: Boolean,
    default: DEFAULT_PROPS.customPositionMenu,
  },
  /**
   * NNLAM 26/02/2020
   * Set lại chiều dài menu dropdown
   */
  customWidth: {
    type: Number,
    default: DEFAULT_PROPS.customWidth,
  },
  /**
   * NNLAM 26/02/2020
   * khoảng cách của button so với lề trên/dưới
   */
  heightOffset: {
    type: Number,
    default: DEFAULT_PROPS.heightOffset,
  },
  /**
   * NNLAM 26/02/2021
   * khoảng cách của button so với lề trái/phải
   */
  widthOffset: {
    type: Number,
    default: DEFAULT_PROPS.widthOffset,
  },
  /**
   * NNLAM 26/02/2021
   * Có show menu khi click vào icon right của button không, mặc định có
   */
  hasShowMenu: {
    type: Boolean,
    default: DEFAULT_PROPS.hasShowMenu,
  },
  /**
   * NNLAM 26/02/2021
   * Có tooltip cho btt không
   */
  tooltipText: {
    type: [String, Object],
    default: DEFAULT_PROPS.tooltipText,
  },
  // border hiển thị cho các button con
  hasBorder: {
    default: DEFAULT_PROPS.hasBorder,
    type: Boolean,
  },
  // border bên ngoài 2 button con
  hasBorderParent: {
    default: DEFAULT_PROPS.hasBorderParent,
    type: Boolean,
  },
  valueField: {
    default: DEFAULT_PROPS.valueField,
    type: String,
  },
  displayField: {
    default: DEFAULT_PROPS.displayField,
    type: String,
  },
  modelValue: {
    type: String,
    default: DEFAULT_PROPS.modelValue,
  },
})

/**
 * Component events definition
 */
const emit = defineEmits([EVENTS.MENU_CLICK, EVENTS.UPDATE_SELECTED])

/**
 * Use composables for modular functionality
 */

// Core multiple select button logic
const componentState = useMultipleSelectButton(props)
const {
  isDisabled,
  internalText,
  cacheSelected,
  toggleMenu,
  hasText,
  hasLeftIcon,
  hasRightIcon,
  hasData,
  isValidType,
  shouldShowBorder,
  shouldShowParentBorder,
  hasCustomPosition,
  customMenuWidth,
  menuHeightOffset,
  menuWidthOffset,
  shouldShowMenu,
  hasTooltip,
  effectiveValueField,
  effectiveDisplayField,
  selectedItems,
  selectedCount,
  hasSelectedItems,
  isItemSelected,
  selectItem,
  deselectItem,
  toggleItemSelection,
  clearSelection
} = componentState

// Event handling - giữ nguyên logic gốc
const { clickItem, isItemChecked } = useMultipleSelectButtonEvents(props, emit, componentState)

// Styling logic - sử dụng computed variables thay vì hard-code
const {
  containerClasses,
  menuWrapperClasses,
  menuWrapperRootClass,
  buttonClasses,
  optionItemClasses,
  checkboxCellClasses,
  checkboxIconClasses,
  getItemIconClasses,
  showData
} = useMultipleSelectButtonStyles(props, componentState)

// Giữ nguyên buttonSplit ref từ code gốc
const buttonSplit = ref(null)

// Expose component API - để tương thích với testing và usage
defineExpose({
  // Giữ nguyên interface gốc
  clickItem,
  toggleMenu,
  cacheSelected,
  internalText,
  
  // Component state
  isDisabled,
  hasData,
  selectedItems,
  selectedCount,
  hasSelectedItems,
  
  // Selection methods
  isItemSelected,
  clearSelection,
  
  // Validation
  isValidType,
  
  // Styling
  containerClasses,
  buttonClasses
})
</script>

<style  lang="scss">
@import '@/assets/scss/components/MsButton.scss';
.mutiple-select-widget-more-container {
  .menu-wrapper-item {
    padding: 0px;
    .option-item {
      gap: 5px;
    }
    .cell-checkbox {
      .icon {
        &.checked {
          &::before {
            left: 2px;
          }
        }
      }
    }
  }
}
.mutiple-select-button {
  // background-color: $primary;
  &.border-split-btn {
    border: 1px solid #afafaf;
    border-radius: 3px;
  }
  button {
    &.primary-button {
      border-end-end-radius: unset;
      border-start-end-radius: unset;
    }
    &:focus-visible {
        z-index: 1;
      }
  }
  .split-icon {
    width: 36px;
    height: 24px;
    margin: 0 !important;
    .icon {
      margin: 0;
      cursor: pointer;
    }
  }

  .context-item {
    height: 36px;
    padding: 2px 8px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    .context-text {
      padding: 0 8px;
    }
    &:hover {
      cursor: pointer;
      background: $hover-bg;
      color: #14aa8a;
    }
  }
  .context-icon {
    margin-right: 8px;
  }
  .color-red {
    color: #ff0000;
  }
  .button-line {
    display: inline-block;
    width: 1px;
    height: 16px;
    background-color: #fff;
  }
}
</style>
